import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Button, Flex, Typography } from 'antd';

import axios from '../../../axios';

import { hasPermission } from '../../../helpers/user';

import { GROUP_TYPE, MAIN_GROUP_TYPE } from '../../../constants/group';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';

import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { fetchAnimals } from '../../../redux/actions/animal';
import { fetchGeofences } from '../../../redux/actions/geofence';
import { fetchLabel, fetchLabels, updateGroup } from '../../../redux/actions/label';

import AppModal from '../../../components/AppModal';
import CancelSaveButtons from '../../../components/CancelSaveButtons';
import LoadingBar from '../../../components/LoadingBar';
import ChooseGeofencesModal from '../../../components/Modals/ChooseGeofencesModal';
import PrevNextButtons from '../../../components/PrevNextButtons';
import { errorToastHandler, successToastHandler, warnToastHandler } from '../../../components/action_notifier';
import UnsavedChangesModal from '../../../components/ui/UnsavedChangesModal';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';
import AppDropdown from '../../ui-components/AppDropdown';

import './styles.scss';

import GroupEditForm from './GroupEditForm';
import GroupLivestock from './GroupLivestock';

const { Title } = Typography;

function Group() {
  const dispatch = useDispatch();
  const history = useHistory();

  const { id } = useParams();

  const formRef = useRef(null);

  const { data: animals, loading: animalsLoading } = useSelector(state => state.animal);
  const { selectOptions: geofenceOptions } = useSelector(state => state.geofence);
  const { label } = useSelector(state => state.label);
  const user = useSelector(state => state.user);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    chooseGeofencesModal: false,
    delete_modal: false,
    edit: false,
    livestock: [],
    isOpenConfirmationModal: false,
    label: {
      animals: [],
      color: '',
      filters: {},
      id: null,
      name: '',
      rules: []
    },
    linked_livestock: [],
    query: '',
    query_rules: '',
    selectAll: false,
    selectedGeofence: {},
    selectedLivestock: []
  });
  const prevQueryRef = useRef(state.query);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (label) {
      setState(prevState => ({
        ...prevState,
        label,
        linked_livestock: label.animals?.map(x => x.id),
        selectedGeofence: label?.geofences?.at(0)
          ? {
              label: label?.geofences?.at(0)?.name,
              value: label?.geofences?.at(0)?.id
            }
          : null
      }));

      getLivestock(label);
    }
  }, [label]);

  useEffect(() => {
    if (label && prevQueryRef.current !== state.query) {
      prevQueryRef.current = state.query;
      getLivestock(label);
    }
  }, [state.query, label]);

  useEffect(() => {
    if (animals?.length) {
      const livestock = [];

      animals.forEach(item => {
        const physicalGroup = item.labels.find(group => group.main_type === MAIN_GROUP_TYPE.PHYSICAL);
        let isIncluded;

        if (state.linked_livestock.includes(item.id)) {
          isIncluded = true;
        } else if (!state.linked_livestock.includes(item.id) && !physicalGroup) {
          isIncluded = false;
        }

        const updatedItem = { ...item, isIncluded };
        livestock.push(updatedItem);
      });

      setState(prevState => ({ ...prevState, livestock }));
    }
  }, [animals]);

  const navigateToThePrevPage = useCallback(() => {
    history.push(`/group/${label?.prev_id}`);
  }, [label?.prev_id]);

  const navigateToTheNextPage = useCallback(() => {
    history.push(`/group/${label?.next_id}`);
  }, [label?.next_id]);

  const handleCancelEditing = useCallback(() => {
    setState(prevState => ({ ...prevState, edit: false }));
    getLabel();
  }, [state.edit]);

  const toggleEditMode = useCallback(() => {
    setState(prevState => ({ ...prevState, edit: !prevState.edit }));
  }, [state.edit]);

  const toggleGeofencesModal = useCallback(() => {
    setState(prevState => ({ ...prevState, chooseGeofencesModal: !prevState.chooseGeofencesModal }));
  }, [state.chooseGeofencesModal]);

  const toggleDeleteModal = useCallback(() => {
    setState(prevState => ({ ...prevState, delete_modal: !prevState.delete_modal }));
  }, [state.delete_modal]);

  const isCustomGroup = label => label.type === GROUP_TYPE.CUSTOM;
  const isVirtualGroup = label => label.type === GROUP_TYPE.VIRTUAL;

  const actions = useMemo(() => {
    const availableActions = [];

    if (hasPermission([PERMISSIONS.LABELS.CREATE], user.permissions)) {
      availableActions.push({
        label: (
          <Link className="app-link" to="/group/new" disabled={!isCustomGroup(state.label)}>
            Add group
          </Link>
        )
      });
    }

    if (hasPermission([PERMISSIONS.LABELS.UPDATE_OWN], user.permissions)) {
      // default and auto-generated groups should not be edited
      if (label?.type === GROUP_TYPE.CUSTOM) {
        availableActions.push(
          {
            label: (
              <Button color="default" size="small" variant="link" onClick={toggleEditMode}>
                Edit group
              </Button>
            )
          });
      }
    }

    if (hasPermission([PERMISSIONS.LABELS.DELETE_OWN], user.permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!isCustomGroup(state.label)}
            onClick={toggleDeleteModal}>
            Archive group
          </Button>
        )
      });
    }

    return availableActions;
  }, [state.label, toggleDeleteModal, toggleEditMode, toggleGeofencesModal, user.permissions]);

  async function fetchData() {
    setLoading(true);
    try {
      await Promise.all([getLabel(), getLabels(), getFences()]);
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    } finally {
      setLoading(false);
    }
  }

  const getLabels = async () => {
    const response = await dispatch(fetchLabels({ farm_ids: [] })).unwrap();
    const groups = response.data
      .filter(item => item.main_type !== MAIN_GROUP_TYPE.COLLECTION)
      .map(item => ({ value: item.id, label: item.name }));

    setState(prevState => ({ ...prevState, groups }));
  };

  const getFences = async () => {
    await dispatch(fetchGeofences({ only_custom: true })).unwrap();
  };

  const getLabel = async () => {
    await dispatch(fetchLabel({ id, params: { query: state.query } })).unwrap();
  };

  const getLivestock = async data => {
    if (!data) return;

    const mainType = data.main_type;
    const groupType = data.type;
    const params = {};

    if (mainType === MAIN_GROUP_TYPE.COLLECTION) {
      params.label_ids = data.collection?.map(item => item.id);
    } else {
      params.farm_ids = data.animals?.map(animal => animal.farm_id);
    }

    if (groupType === 'loss_date') {
      params.with_archived = 'true';
    }

    if (state.query) {
      params.query = state.query;
    }

    await dispatch(fetchAnimals({ params })).unwrap();
  };

  const saveLabel = async () => {
    await dispatch(updateGroup({ id, data: state.label }));
    setState(prevState => ({ ...prevState, edit: false }));
    await getLabel();
    successToastHandler(messages.UPDATED);
    setIsFormDirty(false);
  };

  const deleteLabel = async () => {
    const response = await axios.delete('labels/' + id);

    if (response.status === 200) history.push('/groups');
  };

  const setGroup = changedValues => {
    setState(prevState => ({ ...prevState, label: { ...prevState.label, ...changedValues } }));
  };

  const setGroupFilters = filters => {
    setState(prevState => ({
      ...prevState,
      label: { ...prevState.label, filters: { ...prevState.filters, ...filters } }
    }));
  };

  const setSelectedGeofence = (value, option) => {
    setState(prevState => ({ ...prevState, selectedGeofence: option }));
  };

  const onSaveGeofence = async () => {
    const geofenceId = state.selectedGeofence?.value;

    if (geofenceId) {
      await axios.put(`labels/updateGeofenceInGroup/${id}`, { geofence_id: geofenceId });
      await getLivestock();
    }

    setState(prevState => ({ ...prevState, chooseGeofencesModal: !prevState.chooseGeofencesModal }));
  };

  const breadcrumbItems = [
    {
      title: <Link to="/groups">List of Groups</Link>
    },
    {
      title: `Group ${state.label.name}`
    }
  ];

  const handleSearchChange = query => {
    setState(prevState => ({ ...prevState, query }));
  };

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (submittable) {
          await saveLabel();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        setIsFormDirty(false);
        handleCancelEditing();
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : handleCancelEditing();
  }, [isFormDirty]);

  return (
    <>
      {loading ? (
        <LoadingBar fullscreen={true} />
      ) : (
        <div key={id}>
          {NavigationPrompt}
          <Flex gap="small" vertical={false} justify="space-between" wrap>
            <Flex vertical={true}>
              <Title level={4}>Group {state.label.name}</Title>

              <AppBreadcrumb items={breadcrumbItems} />
            </Flex>
            <Flex gap="small">
              {!state.edit && <AppDropdown label="Actions" items={actions} />}

              {state.edit && (
                <CancelSaveButtons disabled={!submittable} handleCancel={handleCancel} handleSave={saveLabel} />
              )}

              <PrevNextButtons
                nextId={label?.next_id}
                prevId={label?.prev_id}
                handlePrev={navigateToThePrevPage}
                handleNext={navigateToTheNextPage}
              />
            </Flex>
          </Flex>

          <GroupEditForm
            color={state.label?.colour}
            editing={state.edit}
            formRef={formRef}
            group={state.label}
            groups={state.groups}
            handleChangeFormValues={setGroup}
            handleQueryBuilderChange={setGroupFilters}
            onChange={setIsFormDirty}
            setSubmittable={setSubmittable}
          />

          <GroupLivestock
            data={state.livestock}
            groupGeofenceId={state.label?.geofences?.at(0)?.id}
            isCustomGroup={isCustomGroup}
            loading={animalsLoading}
            mainGroupType={state.label?.main_type}
            onUpdateLivestock={getLabel}
            handleSearchChange={handleSearchChange}
          />

          <AppModal
            isOpen={state.delete_modal}
            confirmButtonColor="danger"
            confirmButtonText="Archive"
            title="Archive group"
            handleCancel={toggleDeleteModal}
            handleConfirm={deleteLabel}>
            <div className="py-4">{messages.ARCHIVE_CONFIRMATION('group')}</div>
          </AppModal>

          <ChooseGeofencesModal
            isOpen={state.chooseGeofencesModal}
            options={geofenceOptions}
            value={state.selectedGeofence}
            handleGeofenceChange={setSelectedGeofence}
            onCancel={toggleGeofencesModal}
            onSave={onSaveGeofence}
          />

          <UnsavedChangesModal
            isOpen={modalProps.visible}
            handleCancel={modalProps.onCancel}
            handleConfirm={modalProps.onSave}
            handleDiscard={modalProps.onDiscard}
          />
        </div>
      )}
    </>
  );
}

export default memo(Group);
